import { useCallback, useEffect, useRef, useState } from "react"
import { motion, useDragControls, animate, type PanInfo, type HTMLMotionProps } from "motion/react"

const DISMISS_OFFSET = 72
const DISMISS_VELOCITY = 500
const PULL_START_THRESHOLD = 8

function useIsMobile() {
    const [mobile, setMobile] = useState(
        () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches,
    )

    useEffect(() => {
        const mq = window.matchMedia("(max-width: 767px)")
        const update = () => setMobile(mq.matches)
        mq.addEventListener("change", update)
        return () => mq.removeEventListener("change", update)
    }, [])

    return mobile
}

function isSheetDragBlocked(target: EventTarget | null): boolean {
    if (!(target instanceof Element)) return true
    if (target.closest("[data-sheet-drag-exempt]")) return true
    if (target.closest("button, a, input, textarea, select, label, [role='slider']")) return true
    return false
}

function findScrollableAncestor(target: Element, boundary: HTMLElement | null): HTMLElement | null {
    let el: Element | null = target
    while (el && el !== boundary) {
        if (el instanceof HTMLElement) {
            const { overflowY, overflowX } = getComputedStyle(el)
            if ((overflowY === "auto" || overflowY === "scroll") && el.scrollHeight > el.clientHeight + 1) {
                return el
            }
            if ((overflowX === "auto" || overflowX === "scroll") && el.scrollWidth > el.clientWidth + 1) {
                return el
            }
        }
        el = el.parentElement
    }
    return null
}

type SwipeSheetPanelProps = {
    onDismiss: () => void
    hideHandle?: boolean
    /** Отключить свайп-закрытие (например, когда открыт overlay внутри sheet) */
    disableDrag?: boolean
    children: React.ReactNode
    className?: string
    style?: React.CSSProperties
    motionProps?: HTMLMotionProps<"div">
}

/** Bottom sheet с ручкой — свайп вниз закрывает (mobile) */
export default function SwipeSheetPanel({
    onDismiss,
    hideHandle = false,
    disableDrag = false,
    children,
    className,
    style,
    motionProps,
}: SwipeSheetPanelProps) {
    const dragControls = useDragControls()
    const isMobile = useIsMobile()
    const panelRef = useRef<HTMLDivElement>(null)
    const [isClosing, setIsClosing] = useState(false)
    const pullRef = useRef<{ pointerId: number; startY: number; startX: number; active: boolean } | null>(null)

    const dragEnabled = isMobile && !disableDrag && !isClosing

    const startSheetDrag = useCallback(
        (e: React.PointerEvent | PointerEvent) => {
            if (!dragEnabled) return
            dragControls.start(e)
        },
        [dragControls, dragEnabled],
    )

    const finishPull = useCallback(
        (clientY: number) => {
            const pull = pullRef.current
            pullRef.current = null
            if (!pull?.active || isClosing) return

            const dy = Math.max(0, clientY - pull.startY)
            const el = panelRef.current
            if (!el) return

            if (dy > DISMISS_OFFSET) {
                setIsClosing(true)
                el.style.transform = ""
                animate(el, { x: 0, y: window.innerHeight }, { duration: 0.22, ease: "easeIn" }).then(onDismiss)
            } else {
                el.style.transform = ""
                animate(el, { x: 0, y: 0 }, { type: "spring", stiffness: 400, damping: 35 })
            }
        },
        [isClosing, onDismiss],
    )

    useEffect(() => {
        if (!dragEnabled) return

        const onMove = (e: PointerEvent) => {
            const pull = pullRef.current
            if (!pull || pull.pointerId !== e.pointerId || isClosing) return

            const dy = e.clientY - pull.startY
            const dx = e.clientX - pull.startX

            if (!pull.active) {
                if (dy <= PULL_START_THRESHOLD || dy <= Math.abs(dx)) return
                pull.active = true
            }

            const el = panelRef.current
            if (!el) return
            el.style.transform = `translate3d(0, ${Math.max(0, dy)}px, 0)`
        }

        const onEnd = (e: PointerEvent) => {
            const pull = pullRef.current
            if (!pull || pull.pointerId !== e.pointerId) return
            if (pull.active) {
                finishPull(e.clientY)
            } else {
                pullRef.current = null
            }
        }

        window.addEventListener("pointermove", onMove)
        window.addEventListener("pointerup", onEnd)
        window.addEventListener("pointercancel", onEnd)
        return () => {
            window.removeEventListener("pointermove", onMove)
            window.removeEventListener("pointerup", onEnd)
            window.removeEventListener("pointercancel", onEnd)
        }
    }, [dragEnabled, finishPull, isClosing])

    const handleDragEnd = (_: unknown, info: PanInfo) => {
        if (!dragEnabled || isClosing) return
        if (info.offset.y > DISMISS_OFFSET || info.velocity.y > DISMISS_VELOCITY) {
            setIsClosing(true)
            const el = panelRef.current
            if (el) {
                animate(el, { x: 0, y: window.innerHeight }, { duration: 0.22, ease: "easeIn" }).then(onDismiss)
            } else {
                onDismiss()
            }
        }
    }

    const handleContentPointerDownCapture = useCallback(
        (e: React.PointerEvent) => {
            if (!dragEnabled || isSheetDragBlocked(e.target)) return

            const scrollable = findScrollableAncestor(e.target as Element, panelRef.current)
            if (scrollable) {
                if (scrollable.scrollTop <= 0) {
                    pullRef.current = {
                        pointerId: e.pointerId,
                        startY: e.clientY,
                        startX: e.clientX,
                        active: false,
                    }
                }
                return
            }

            startSheetDrag(e)
        },
        [dragEnabled, startSheetDrag],
    )

    return (
        <motion.div
            ref={panelRef}
            className={`max-w-full overflow-x-hidden ${className ?? ""}`}
            style={style}
            drag={dragEnabled ? "y" : false}
            dragControls={dragControls}
            dragListener={false}
            dragDirectionLock
            dragConstraints={{ top: 0, bottom: 0, left: 0, right: 0 }}
            dragElastic={{ top: 0, bottom: 0.55, left: 0, right: 0 }}
            dragMomentum={false}
            onDragEnd={handleDragEnd}
            {...motionProps}>
            {(!hideHandle && dragEnabled) && (
                <div
                    className="flex min-h-[52px] shrink-0 cursor-grab items-center justify-center pt-3 pb-2 active:cursor-grabbing"
                    style={{ touchAction: "none" }}
                    onPointerDown={startSheetDrag}>
                    <div className="h-1.5 w-12 rounded-full bg-white/30" />
                </div>
            )}
            {isMobile ? (
                <div
                    className="flex min-h-0 w-full min-w-0 max-w-full flex-1 flex-col overflow-hidden"
                    onPointerDownCapture={handleContentPointerDownCapture}>
                    {children}
                </div>
            ) : (
                children
            )}
        </motion.div>
    )
}
